import type { CaseCptLine, Laterality, SurgeonTemplate } from '@/lib/templates/types';
import { evaluateCptProofMap } from '@/lib/proofmap/proofMapEngine';
import { getProofMap } from '@/lib/proofmap/orthopedicRules';
import type { RequirementMatchResult } from '@/lib/proofmap/types';
import {
  buildLocalCptSearchCatalog,
  descriptionForCode,
  searchCptCatalog,
  type CptSearchEntry,
  type CptSearchSource,
} from '@/lib/cptSearch/catalog';

export type PostOpCptCatalogSource = CptSearchSource;
export type PostOpCptCatalogEntry = CptSearchEntry;

export type PostOpCoverageStatus = 'documented' | 'needs_sentence' | 'review' | 'no_rules';

export interface NcciPairRuleInput {
  cpt_a: string;
  cpt_b: string;
  primary_when_together?: string | null;
  reason?: string | null;
  bundling_rationale?: string | null;
  mutually_exclusive?: boolean | null;
  modifier_indicator?: string | number | null;
  source_version?: string | null;
  active?: boolean | null;
}

const DISCOVERY_CODE_GROUPS = {
  knee_mua: ['20610', '29875', '29877', '29874', '29881', '29882'],
  knee_arthroscopy: ['29877', '29875', '29876', '29874', '29879', '29882', '20610'],
  knee_arthroplasty: ['20610', '27331', '20680', '27435', '27438', '27310'],
  knee_extensor: ['27386', '20680', '20610', '27331', '27310', '27435'],
  shoulder: ['29826', '29824', '29828', '29823', '29822', '29807', '23430', '23120'],
  hand_wrist: ['20526', '64719', '64718', '64708', '25115', '26145'],
} as const;

export interface PostOpCoverageRequirement {
  id: string;
  cptCode: string;
  cptDescription: string;
  label: string;
  status: Exclude<PostOpCoverageStatus, 'no_rules'>;
  suggestedLine: string;
  evidenceSnippet?: string;
}

export interface PostOpCoverageResult {
  code: string;
  description: string;
  status: PostOpCoverageStatus;
  requirements: PostOpCoverageRequirement[];
}

function normalizeCode(code: string | number | null | undefined): string {
  return String(code || '').trim().toUpperCase();
}

function normalizeDiscoveryText(value: string | null | undefined): string {
  return String(value || '').toLowerCase();
}

function hasAnyNeedle(text: string, needles: string[]): boolean {
  return needles.some(needle => text.includes(needle));
}

function isModifierAllowed(rule: NcciPairRuleInput): boolean {
  if (rule.mutually_exclusive) return false;
  const indicator = String(rule.modifier_indicator ?? '').trim().toUpperCase();
  return indicator !== '0' && indicator !== 'N' && indicator !== 'NO';
}

function ruleReason(rule: NcciPairRuleInput): string | undefined {
  return rule.bundling_rationale?.trim() || rule.reason?.trim() || undefined;
}

function ruleIsActive(rule: NcciPairRuleInput): boolean {
  return rule.active !== false;
}

function findNcciRuleForPair(
  ncciPairRules: readonly NcciPairRuleInput[] | null | undefined,
  baseCodes: Iterable<string>,
  candidateCode: string,
): NcciPairRuleInput | null {
  if (!ncciPairRules) return null;
  const bases = new Set(Array.from(baseCodes).map(normalizeCode));
  const candidate = normalizeCode(candidateCode);

  return ncciPairRules.find((rule) => {
    if (!ruleIsActive(rule)) return false;
    const cptA = normalizeCode(rule.cpt_a);
    const cptB = normalizeCode(rule.cpt_b);
    return (bases.has(cptA) && cptB === candidate) || (bases.has(cptB) && cptA === candidate);
  }) || null;
}

function ncciCandidateCodes(
  ncciPairRules: readonly NcciPairRuleInput[],
  baseCodes: Iterable<string>,
): string[] {
  const bases = new Set(Array.from(baseCodes).map(normalizeCode));
  const candidates: string[] = [];

  ncciPairRules.forEach((rule) => {
    if (!ruleIsActive(rule) || rule.mutually_exclusive) return;
    const cptA = normalizeCode(rule.cpt_a);
    const cptB = normalizeCode(rule.cpt_b);
    if (bases.has(cptA) && cptB) candidates.push(cptB);
    if (bases.has(cptB) && cptA) candidates.push(cptA);
  });

  return Array.from(new Set(candidates));
}

function inferDiscoveryCodes(params: {
  primaryCpt?: string | null;
  template?: SurgeonTemplate | null;
}): readonly string[] {
  const primaryCpt = normalizeCode(params.primaryCpt || params.template?.primary_cpts?.[0]?.code);
  const templateText = normalizeDiscoveryText([
    params.template?.body_region,
    params.template?.template_name,
    ...(params.template?.primary_cpts || []).map(line => line.description),
  ].filter(Boolean).join(' '));

  if (primaryCpt === '27570' || hasAnyNeedle(templateText, ['manipulation', 'arthrofibrosis'])) {
    return DISCOVERY_CODE_GROUPS.knee_mua;
  }

  if (['29874', '29875', '29876', '29877', '29879', '29880', '29881', '29882'].includes(primaryCpt) ||
    hasAnyNeedle(templateText, ['knee arthroscopy', 'menisc', 'chondroplasty', 'loose body'])) {
    return DISCOVERY_CODE_GROUPS.knee_arthroscopy;
  }

  if (['27447', '27486', '27487'].includes(primaryCpt) ||
    hasAnyNeedle(templateText, ['total knee', 'arthroplasty', 'replacement'])) {
    return DISCOVERY_CODE_GROUPS.knee_arthroplasty;
  }

  if (['27385', '27386'].includes(primaryCpt) ||
    hasAnyNeedle(templateText, ['quadriceps', 'extensor mechanism', 'patellar tendon'])) {
    return DISCOVERY_CODE_GROUPS.knee_extensor;
  }

  if (['29806', '29807', '29822', '29823', '29824', '29826', '29827', '29828', '23120', '23410', '23412', '23420', '23430', '23470', '23472'].includes(primaryCpt) ||
    hasAnyNeedle(templateText, ['shoulder', 'rotator cuff', 'biceps', 'subacromial', 'clavicle'])) {
    return DISCOVERY_CODE_GROUPS.shoulder;
  }

  if (primaryCpt === '64721' || hasAnyNeedle(templateText, ['carpal tunnel', 'wrist', 'hand', 'median nerve'])) {
    return DISCOVERY_CODE_GROUPS.hand_wrist;
  }

  return [];
}

function formatCpt(line: Pick<CaseCptLine, 'code' | 'modifiers'>): string {
  const code = normalizeCode(line.code);
  return `${code}${line.modifiers.length ? `-${line.modifiers.join('-')}` : ''}`;
}

function lateralityLabel(laterality: Laterality | null): string {
  if (laterality === 'right') return 'right';
  if (laterality === 'left') return 'left';
  if (laterality === 'bilateral') return 'bilateral';
  return 'the operative';
}

export function buildPostOpCptSearchCatalog(params: {
  authorizedLines: CaseCptLine[];
  template?: SurgeonTemplate | null;
  primaryCpt?: string | null;
}): PostOpCptCatalogEntry[] {
  return buildLocalCptSearchCatalog(params);
}

export function searchPostOpCptCatalog(
  catalog: PostOpCptCatalogEntry[],
  query: string,
  limit = 12,
): PostOpCptCatalogEntry[] {
  return searchCptCatalog(catalog, query, { limit });
}

export function buildPlausiblePostOpDiscoverySuggestions(params: {
  catalog: PostOpCptCatalogEntry[];
  primaryCpt?: string | null;
  template?: SurgeonTemplate | null;
  activeCodes?: Iterable<string>;
  limit?: number;
}): PostOpCptCatalogEntry[] {
  const activeCodes = new Set(Array.from(params.activeCodes || []).map(normalizeCode));
  const primaryCpt = normalizeCode(params.primaryCpt || params.template?.primary_cpts?.[0]?.code);
  if (primaryCpt) activeCodes.add(primaryCpt);

  const catalogByCode = new Map(params.catalog.map(entry => [normalizeCode(entry.code), entry]));
  const discoveryCodes = inferDiscoveryCodes(params);

  return discoveryCodes
    .filter(code => !activeCodes.has(normalizeCode(code)))
    .map((code, index): PostOpCptCatalogEntry => {
      const normalizedCode = normalizeCode(code);
      const existing = catalogByCode.get(normalizedCode);
      return {
        code: normalizedCode,
        description: descriptionForCode(normalizedCode, existing?.description),
        category: existing?.category ?? 'Intra-op discovery',
        source: existing?.source ?? 'known',
        priority: 92 - index,
        preAuthorized: existing?.preAuthorized ?? false,
        plausible: true,
        notes: existing?.notes,
        defaultModifiers: existing?.defaultModifiers || [],
      };
    })
    .slice(0, params.limit ?? 6);
}

export function buildNcciAwarePostOpDiscoverySuggestions(params: {
  catalog: PostOpCptCatalogEntry[];
  primaryCpt?: string | null;
  authorizedLines?: CaseCptLine[];
  template?: SurgeonTemplate | null;
  activeCodes?: Iterable<string>;
  ncciPairRules: readonly NcciPairRuleInput[] | null;
  limit?: number;
}): PostOpCptCatalogEntry[] {
  if (params.ncciPairRules === null) {
    return buildPlausiblePostOpDiscoverySuggestions({
      catalog: params.catalog,
      primaryCpt: params.primaryCpt,
      template: params.template,
      activeCodes: params.activeCodes,
      limit: params.limit,
    });
  }

  const activeCodes = new Set(Array.from(params.activeCodes || []).map(normalizeCode));
  const primaryCpt = normalizeCode(params.primaryCpt || params.template?.primary_cpts?.[0]?.code);
  if (primaryCpt) activeCodes.add(primaryCpt);

  const baseCodes = new Set([
    primaryCpt,
    ...(params.authorizedLines || []).map(line => normalizeCode(line.code)),
  ].filter(Boolean));
  if (baseCodes.size === 0 && primaryCpt) baseCodes.add(primaryCpt);

  const catalogByCode = new Map(params.catalog.map(entry => [normalizeCode(entry.code), entry]));
  const candidateCodes = ncciCandidateCodes(params.ncciPairRules || [], baseCodes);
  const limit = params.limit ?? 6;

  const ncciSuggestions = candidateCodes
    .filter(code => !activeCodes.has(normalizeCode(code)))
    .map((code, index): PostOpCptCatalogEntry | null => {
      const normalizedCode = normalizeCode(code);
      const rule = findNcciRuleForPair(params.ncciPairRules, baseCodes, normalizedCode);
      if (!rule || rule.mutually_exclusive) return null;
      const existing = catalogByCode.get(normalizedCode);
      const modifierAllowed = isModifierAllowed(rule);
      return {
        code: normalizedCode,
        description: descriptionForCode(normalizedCode, existing?.description),
        category: existing?.category ?? 'NCCI-supported discovery',
        source: existing?.source ?? 'known',
        priority: Math.max(existing?.priority ?? 0, 110 - index),
        preAuthorized: existing?.preAuthorized ?? false,
        plausible: true,
        ncciSupported: true,
        ncciReason: ruleReason(rule),
        modifierAllowed,
        reviewRequired: !modifierAllowed,
        notes: [existing?.notes, ruleReason(rule)].filter(Boolean).join(' '),
        defaultModifiers: existing?.defaultModifiers?.length
          ? existing.defaultModifiers
          : modifierAllowed ? ['XS'] : [],
      };
    })
    .filter((entry): entry is PostOpCptCatalogEntry => Boolean(entry))
    .sort((a, b) => {
      if (a.preAuthorized !== b.preAuthorized) return a.preAuthorized ? -1 : 1;
      if (a.ncciSupported !== b.ncciSupported) return a.ncciSupported ? -1 : 1;
      if (a.plausible !== b.plausible) return a.plausible ? -1 : 1;
      if (a.reviewRequired !== b.reviewRequired) return a.reviewRequired ? 1 : -1;
      return b.priority - a.priority || a.code.localeCompare(b.code);
    });

  if (ncciSuggestions.length >= limit) {
    return ncciSuggestions.slice(0, limit);
  }

  const fallbackActiveCodes = new Set([
    ...activeCodes,
    ...ncciSuggestions.map(entry => normalizeCode(entry.code)),
  ]);
  const fallbackSuggestions = buildPlausiblePostOpDiscoverySuggestions({
    catalog: params.catalog,
    primaryCpt: primaryCpt || params.primaryCpt,
    template: params.template,
    activeCodes: fallbackActiveCodes,
    limit: limit - ncciSuggestions.length,
  }).map((entry, index): PostOpCptCatalogEntry => ({
    ...entry,
    priority: Math.min(entry.priority, 89 - index),
    plausible: true,
    ncciSupported: entry.ncciSupported ?? false,
    modifierAllowed: entry.modifierAllowed ?? Boolean(entry.defaultModifiers?.length),
    reviewRequired: false,
    notes: [
      entry.notes,
      'Procedure-specific intra-op discovery suggestion.',
    ].filter(Boolean).join(' '),
  }));

  return [...ncciSuggestions, ...fallbackSuggestions].slice(0, limit);
}

export function rankPostOpSearchResultsWithNcci(params: {
  results: PostOpCptCatalogEntry[];
  primaryCpt?: string | null;
  authorizedLines?: CaseCptLine[];
  activeCodes?: Iterable<string>;
  ncciPairRules: readonly NcciPairRuleInput[] | null;
}): PostOpCptCatalogEntry[] {
  if (params.ncciPairRules === null) return params.results;

  const activeCodes = new Set(Array.from(params.activeCodes || []).map(normalizeCode));
  const baseCodes = new Set([
    normalizeCode(params.primaryCpt),
    ...(params.authorizedLines || []).map(line => normalizeCode(line.code)),
  ].filter(Boolean));

  return params.results
    .filter(entry => !activeCodes.has(normalizeCode(entry.code)))
    .map((entry): PostOpCptCatalogEntry => {
      if (entry.preAuthorized) {
        return {
          ...entry,
          plausible: true,
          ncciSupported: true,
          reviewRequired: false,
        };
      }

      const rule = findNcciRuleForPair(params.ncciPairRules, baseCodes, entry.code);
      if (!rule || rule.mutually_exclusive) {
        return {
          ...entry,
          plausible: false,
          ncciSupported: false,
          modifierAllowed: false,
          reviewRequired: true,
        };
      }

      const modifierAllowed = isModifierAllowed(rule);
      return {
        ...entry,
        priority: entry.priority + 30,
        plausible: modifierAllowed,
        ncciSupported: true,
        ncciReason: ruleReason(rule),
        modifierAllowed,
        reviewRequired: !modifierAllowed,
        defaultModifiers: entry.defaultModifiers?.length
          ? entry.defaultModifiers
          : modifierAllowed ? ['XS'] : [],
      };
    })
    .sort((a, b) => {
      if (a.preAuthorized !== b.preAuthorized) return a.preAuthorized ? -1 : 1;
      if (a.ncciSupported !== b.ncciSupported) return a.ncciSupported ? -1 : 1;
      if (a.reviewRequired !== b.reviewRequired) return a.reviewRequired ? 1 : -1;
      if (a.plausible !== b.plausible) return a.plausible ? -1 : 1;
      return b.priority - a.priority || a.code.localeCompare(b.code);
    });
}

function suggestedLineForRequirement(requirement: RequirementMatchResult, cptCode: string): string {
  const label = requirement.label.toLowerCase();

  if (label.includes('rotator cuff repair')) {
    return 'The rotator cuff tear was mobilized and repaired to the anatomic footprint using suture-anchor fixation, with the tendon securely reduced and fixed at completion.';
  }
  if (label.includes('rotator cuff tear')) {
    return 'A full-thickness rotator cuff tear involving the supraspinatus tendon was identified during arthroscopic evaluation.';
  }
  if (label.includes('arthroscopic')) {
    return 'The procedure was performed arthroscopically through standard portals with complete visualization of the operative compartment.';
  }
  if (label.includes('subacromial')) {
    return 'The arthroscope was redirected into the subacromial space, where subacromial decompression and acromioplasty were performed as a distinct compartment of work.';
  }
  if (label.includes('distinct')) {
    return 'This work was performed in a distinct anatomical compartment separate from the primary repair and was separately documented for CPT reporting.';
  }
  if (label.includes('biceps')) {
    return 'Biceps tendon pathology was identified and a biceps tenodesis was performed with secure fixation of the tendon.';
  }
  if (label.includes('distal clavicle')) {
    return 'The acromioclavicular joint was evaluated and a distal clavicle excision was performed to address the separate AC joint pathology.';
  }
  if (label.includes('menisc')) {
    return 'The meniscal tear was identified arthroscopically and treated with partial meniscectomy of the unstable torn meniscal tissue.';
  }
  if (label.includes('total knee') || label.includes('compartment')) {
    return 'The total knee arthroplasty was performed with preparation of the distal femur and proximal tibia and implantation of the final components with stable alignment.';
  }

  return `For CPT ${cptCode}, ${requirement.label.toLowerCase()} was performed and documented as part of the operative procedure.`;
}

function mapRequirementStatus(status: RequirementMatchResult['status']): PostOpCoverageRequirement['status'] {
  if (status === 'SATISFIED') return 'documented';
  if (status === 'AMBIGUOUS') return 'review';
  return 'needs_sentence';
}

export function evaluatePostOpDictationCoverage(
  activeLines: CaseCptLine[],
  transcript: string,
): PostOpCoverageResult[] {
  return activeLines.map((line) => {
    const code = normalizeCode(line.code);
    const proofMap = getProofMap(code);
    const description = descriptionForCode(code, line.description);

    if (!proofMap) {
      return {
        code,
        description,
        status: 'no_rules',
        requirements: [],
      };
    }

    const result = evaluateCptProofMap(proofMap, [
      { text: transcript || '', source: 'DICTATION' },
    ]);

    const requirements = result.requirement_results.map((requirement): PostOpCoverageRequirement => ({
      id: requirement.requirement_id,
      cptCode: code,
      cptDescription: description,
      label: requirement.label,
      status: mapRequirementStatus(requirement.status),
      suggestedLine: suggestedLineForRequirement(requirement, code),
      evidenceSnippet: requirement.all_snippets[0]?.matched_text,
    }));

    const hasReview = requirements.some(requirement => requirement.status === 'review');
    const hasMissing = requirements.some(requirement => requirement.status === 'needs_sentence');

    return {
      code,
      description,
      status: hasReview ? 'review' : hasMissing ? 'needs_sentence' : 'documented',
      requirements,
    };
  });
}

export function appendSuggestedDictationLine(
  currentTranscript: string,
  requirement: PostOpCoverageRequirement,
): string {
  const existing = currentTranscript.trim();
  if (existing.toLowerCase().includes(requirement.suggestedLine.toLowerCase())) {
    return existing;
  }
  return existing ? `${existing}\n\n${requirement.suggestedLine}` : requirement.suggestedLine;
}

function genericProcedureLine(line: CaseCptLine): string {
  const code = normalizeCode(line.code);
  const proofMap = getProofMap(code);
  if (proofMap) {
    return proofMap.requirement_elements
      .map(requirement => suggestedLineForRequirement({
        ...requirement,
        status: 'NOT_FOUND',
        rule_results: [],
        all_snippets: [],
        has_contradiction: false,
        best_confidence: null,
        explanation: '',
      }, code))
      .join(' ');
  }
  return `${line.description || `CPT ${code}`} was performed and documented in standard operative fashion.`;
}

function goldStandardProcedureLine(line: CaseCptLine, side: string): string | null {
  const code = normalizeCode(line.code);
  const sideText = side === 'the operative' ? 'operative' : side;

  switch (code) {
    case '27570':
      return 'The patient was placed under general anesthesia to achieve complete muscle relaxation. Pre-manipulation range of motion was evaluated and found to be rigidly restricted by postoperative arthrofibrosis. Gentle, sustained flexion pressure was applied to the knee joint, with audible and palpable lysis of fibrotic adhesions. Post-manipulation range of motion improved to 120 degrees of flexion and full extension. There was no evidence of fracture, ligamentous compromise, or patellar tendon avulsion during the manipulation.';

    case '27385':
      return `A longitudinal anterior knee incision was made over the ${sideText} extensor mechanism. Dissection was carried through subcutaneous tissue to the disrupted quadriceps/extensor mechanism. Intraoperative findings confirmed traumatic disruption of the distal quadriceps tendon fibers and VMO insertion with extensor mechanism incompetence. The tendon edges were mobilized and debrided to healthy tissue. The quadriceps tendon and VMO were repaired back to the superior pole/extensor mechanism using heavy nonabsorbable sutures in a locking configuration, restoring continuity and tension. The repair was taken through gentle range of motion and remained stable without gapping. Hemostasis was obtained, the wound was irrigated and closed in layered fashion, and the knee was placed in extension.`;

    case '29827':
      return 'Diagnostic shoulder arthroscopy was performed through standard portals. A full-thickness rotator cuff tear involving the supraspinatus tendon was identified. The rotator cuff footprint was prepared to bleeding bone, and the tendon was mobilized and reduced to its anatomic footprint. Rotator cuff repair was completed with suture anchor fixation; the tendon was securely reduced and fixed at completion.';

    case '29826':
      return 'The arthroscope was redirected from the glenohumeral joint into the subacromial space. Extensive subacromial bursitis and outlet impingement morphology were identified. Subacromial decompression and acromioplasty were performed with shaver and burr in the subacromial space as distinct work beyond the rotator cuff repair.';

    case '29828':
      return 'Biceps tendon pathology was identified, including significant long-head biceps instability and tendinosis. Biceps tenodesis was performed; the biceps tendon was secured with fixation, and final tension and stability were confirmed.';

    case '23420':
      return `An open deltoid-splitting approach was performed for the ${sideText} shoulder, and the deltoid was protected throughout exposure. The subacromial space was exposed. A chronic full-thickness rotator cuff tear was identified, and the supraspinatus tendon was mobilized. Acromioplasty and subacromial decompression were performed to remove the anterior acromial spur and complete outlet decompression. The cuff was repaired to the greater tuberosity using suture anchors and heavy nonabsorbable sutures, and the tendon was secured to bone without undue tension.`;

    case '23120':
      return 'The acromioclavicular joint was evaluated as a separate AC joint structure from the cuff repair. AC joint arthrosis and distal clavicle osteophytes were identified. Distal clavicle resection/partial claviculectomy was performed, removing approximately 8 mm of distal clavicle while preserving stability. This work addressed distinct AC joint pathology separate from the rotator cuff repair.';

    case '64721':
      return `A palmar incision was made over the ${sideText} carpal tunnel, and the carpal tunnel was exposed. The transverse carpal ligament was completely released under direct visualization, with the release carried distally and proximally until there was no residual compression. The median nerve was visualized and median neuroplasty was completed; the nerve was freely decompressed at completion. Hemostasis was obtained and the incision was closed in layered fashion.`;

    case '27447':
      return `A standard ${sideText} total knee arthroplasty was performed. The distal femur and proximal tibia were prepared with appropriate bone resections addressing the medial and lateral compartments. Trialing confirmed stable alignment, balanced flexion-extension gaps, and patellar tracking. Final femoral component, tibial component, polyethylene insert, and patellar component were implanted with stable fixation.`;

    case '27435':
      return 'A fixed flexion contracture and posterior capsular tightness were identified after bone preparation and trialing. A posterior capsular release with collateral ligament balancing was performed as distinct contracture-balancing work beyond routine exposure. The flexion-extension gap was reassessed after release and the extension gap was balanced.';

    case '29880':
      return `Diagnostic arthroscopy of the ${sideText} knee was performed through standard portals. Arthroscopic evaluation identified medial meniscus tear and lateral meniscus tear pathology. Partial medial meniscectomy and partial lateral meniscectomy were performed using basket forceps and shaver, resecting unstable meniscal tissue back to stable peripheral rims in both compartments.`;

    case '20610':
      return `A major joint injection was performed at the ${sideText} knee joint using standard sterile technique. Medication was injected into the joint space after confirmation of appropriate needle placement, and the patient tolerated the injection without complication.`;

    default:
      return null;
  }
}

export function buildGeneratedPostOpDictation(params: {
  patientName: string;
  procedureName: string;
  activeLines: CaseCptLine[];
  laterality: Laterality | null;
  positioning: string;
  ebl: string;
  operativeTime: string;
}): string {
  const side = lateralityLabel(params.laterality);
  const procedureLines = params.activeLines.map((line) => (
    `CPT ${formatCpt(line)}: ${goldStandardProcedureLine(line, side) || genericProcedureLine(line)}`
  ));

  return [
    `Operative report for ${params.patientName}. The patient was positioned in ${params.positioning} position and the ${side} side was prepped and draped in sterile fashion.`,
    `Procedure performed: ${params.procedureName}.`,
    ...procedureLines,
    `Estimated blood loss was ${params.ebl}. Operative time was ${params.operativeTime}. There were no intraoperative complications. The patient tolerated the procedure well and was transferred to recovery in stable condition.`,
  ].join('\n\n');
}
